import { ErrorBanner } from '../ui/index.jsx'

interface DepartmentStepProps {
  templateApplied: boolean
  deptName: string
  setDeptName: (name: string) => void
  deptColor: string
  setDeptColor: (color: string) => void
  saveDept: () => void
  error: string
  setError: (err: string) => void
  loading: boolean
  goNext: () => void
  t: (key: string) => string
}

const DEPT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4',
]

export default function DepartmentStep({
  templateApplied, deptName, setDeptName, deptColor, setDeptColor,
  saveDept, error, setError, loading, goNext, t,
}: DepartmentStepProps) {
  return (
    <div>
      <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
        {templateApplied ? t('addAnotherDept') : t('createFirstDept')}
      </h2>
      <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', marginBottom: 32 }}>
        {templateApplied
          ? t('templateDepartmentsReady')
          : t('departmentsKeepOrganized')}
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          {t('departmentNameLabel')} *
        </label>
        <input
          type="text"
          value={deptName}
          onChange={e => setDeptName(e.target.value)}
          placeholder="e.g. Sound"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && saveDept()}
          style={{
            width: '100%', padding: '11px 14px', fontSize: 'var(--text-md)', boxSizing: 'border-box',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: 8, color: 'var(--text-primary)', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
        />
      </div>

      <div style={{ marginBottom: 28 }}>
        <label style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 10 }}>
          {t('departmentColor')}
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DEPT_COLORS.map(c => (
            <button
              key={c}
              onClick={() => setDeptColor(c)}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: c, border: deptColor === c ? '3px solid var(--text-primary)' : '3px solid transparent',
                cursor: 'pointer', transition: 'transform 0.15s',
                outline: deptColor === c ? `2px solid ${c}` : 'none',
                outlineOffset: 2,
              }}
            />
          ))}
        </div>
      </div>

      {/* Preview */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28,
        background: 'var(--bg-elevated)', borderRadius: 8, padding: '10px 14px',
      }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: deptColor, flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--text-primary)' }}>
          {deptName || t('yourDepartment')}
        </span>
      </div>

      <ErrorBanner style={{ fontSize: 'var(--text-caption)', marginBottom: 12 }}>{error}</ErrorBanner>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => { setError(''); goNext() }}
          style={{
            flex: 1, padding: '13px', borderRadius: 10,
            background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
            border: '1px solid var(--border-default)', fontSize: 'var(--text-base)',
            cursor: 'pointer',
          }}
        >
          {t('skipForNow')}
        </button>
        <button
          onClick={saveDept}
          disabled={loading}
          style={{
            flex: 2, padding: '13px', borderRadius: 10,
            background: loading ? 'var(--bg-elevated)' : 'var(--accent)',
            color: loading ? 'var(--text-muted)' : 'var(--text-inverse)',
            border: 'none', fontSize: 'var(--text-md)', fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? t('creatingDepartment') : t('createDepartment')}
        </button>
      </div>
    </div>
  )
}
